/**
 * LocationChangeCard Component
 * Displays a single location's change details in an expandable card.
 */

import { useState } from 'react';
import { ChevronDown, ExternalLink, AlertCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { SyncLogDetail, SyncLogChangeType, LocationChanges } from '@/types/api';
import { FieldDiff } from './FieldDiff';
import { ImageDiff } from './ImageDiff';
import { AmenityDiff } from './AmenityDiff';

interface LocationChangeCardProps {
  detail: SyncLogDetail;
}

/**
 * Get badge color and text for change type.
 */
function getChangeTypeBadge(changeType: SyncLogChangeType): {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  text: string;
  className?: string;
} {
  switch (changeType) {
    case 'created':
      return { variant: 'default', text: 'Created', className: 'bg-green-500 hover:bg-green-500' };
    case 'updated':
      return { variant: 'default', text: 'Updated', className: 'bg-blue-500 hover:bg-blue-500' };
    case 'unchanged':
      return { variant: 'secondary', text: 'Unchanged' };
    case 'deleted':
      return { variant: 'destructive', text: 'Deleted' };
    case 'error':
      return { variant: 'destructive', text: 'Error' };
    default:
      return { variant: 'outline', text: changeType };
  }
}

/**
 * Check if a field is an image field (partnerImages).
 */
function isImageField(fieldName: string): boolean {
  return fieldName === 'partnerImages';
}

/**
 * Check if a field is an amenity field.
 */
function isAmenityField(fieldName: string): boolean {
  return fieldName === 'amenities';
}

export function LocationChangeCard({ detail }: LocationChangeCardProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(false);
  const { variant, text, className } = getChangeTypeBadge(detail.changeType);

  // Only show expand button for items with changes or errors
  const canExpand = detail.changeType === 'updated' || detail.changeType === 'error';

  // Get changes object safely
  const changes: LocationChanges = detail.changes ?? {};
  const changeFields = Object.keys(changes);

  // Separate regular fields from special fields (images, amenities)
  const regularFields = changeFields.filter(
    (f) => !isImageField(f) && !isAmenityField(f)
  );
  const imageField = changeFields.find(isImageField);
  const amenityField = changeFields.find(isAmenityField);

  return (
    <div
      className="rounded-lg border bg-card"
      data-testid={`sync-log-detail-row-${detail.id}`}
    >
      {/* Header - always visible */}
      <button
        type="button"
        onClick={() => { if (canExpand) setIsExpanded(!isExpanded); }}
        disabled={!canExpand}
        className={cn(
          'flex w-full items-center justify-between p-3 text-left',
          canExpand && 'hover:bg-muted/50 cursor-pointer',
          !canExpand && 'cursor-default'
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <Badge variant={variant} className={className}>
            {text}
          </Badge>
          <div className="flex flex-col min-w-0">
            <span className="font-medium truncate">{detail.locationName}</span>
            <span className="text-xs text-muted-foreground truncate">
              {detail.externalId}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {detail.syncedLocationId && (
            <a
              href={`/synced-locations/${detail.syncedLocationId}`}
              onClick={(e) => { e.stopPropagation(); }}
              className="text-muted-foreground hover:text-foreground p-1"
              title="View location"
            >
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          {canExpand && (
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform duration-200',
                isExpanded && 'rotate-180'
              )}
            />
          )}
        </div>
      </button>

      {/* Expanded content */}
      {isExpanded && canExpand && (
        <div className="border-t px-3 pb-3 pt-2">
          {detail.changeType === 'error' && detail.errorMessage && (
            <div className="flex items-start gap-2 p-2 rounded-md bg-destructive/10 text-destructive text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <span>{detail.errorMessage}</span>
            </div>
          )}

          {detail.changeType === 'updated' && (
            <div className="space-y-2">
              {/* Regular field diffs */}
              {regularFields.map((fieldName) => {
                const change = changes[fieldName];
                if (!change) return null;
                return (
                  <FieldDiff
                    key={fieldName}
                    fieldName={fieldName}
                    oldValue={change.old}
                    newValue={change.new}
                  />
                );
              })}

              {/* Image diff */}
              {imageField && changes[imageField] && (
                <div className="pt-2 border-t">
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    Images
                  </div>
                  <ImageDiff
                    removed={(changes[imageField].old as string[] | undefined) ?? []}
                    added={(changes[imageField].new as string[] | undefined) ?? []}
                  />
                </div>
              )}

              {/* Amenity diff */}
              {amenityField && changes[amenityField] && (
                <div className="pt-2 border-t">
                  <div className="text-xs font-medium text-muted-foreground mb-2">
                    Amenities
                  </div>
                  <AmenityDiff
                    removed={(changes[amenityField].old as string[] | undefined) ?? []}
                    added={(changes[amenityField].new as string[] | undefined) ?? []}
                  />
                </div>
              )}

              {changeFields.length === 0 && (
                <div className="text-sm text-muted-foreground">
                  No detailed changes available
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
